import React, { useEffect, useRef, useCallback, useState } from "react";
import { APIProvider, Map, useMap } from "@vis.gl/react-google-maps";
import { connect } from "react-redux";
import uuidv4 from "uuid/v4";

import LocationMaster from "./LocationMaster";
import HazardPoints from "./HazardPoints";
import HistoryPoint from "./ImpactHazard/HistoryPoint";
import RouteMaster from "./RouteMaster";
import RouteSuggestMap from "./RouteSuggestMap";
import DrawingMap from "./DrawingMap";
import SearchPlace from "./SearchPlace";
import MapLoadingBox from "./MapLoadingBox";
import DistanceMatrixMap from "./DistanceMatrixMap";

import { CLEAR_SELECTING, mapZoomChanged, mapCenterChanged } from "../../reducers/map";
import Configs from "../../config/config";

const env = process.env.NODE_ENV;
const href = () => window.location.href;

// Rendered as a CHILD of <Map>, so useMap() resolves the real google.maps.Map instance.
function MapContents(props) {
  const map = useMap();
  const [fanZoom, setFanZoom] = useState(null); // eslint-disable-line no-unused-vars
  const prevCenter = useRef(null);

  const setBound = useCallback(
    (bounds) => {
      if (!map || !bounds) return;
      try {
        const latLngBounds = new window.google.maps.LatLngBounds();
        let count = 0;
        bounds.forEach((b) => {
          if (b.lat && b.lng) {
            latLngBounds.extend(new window.google.maps.LatLng(b.lat, b.lng));
            count++;
          }
        });
        if (count > 0) map.fitBounds(latLngBounds);
      } catch (e) {}
    },
    [map]
  );

  useEffect(() => {
    if (map && props.bounds && props.bounds.length > 0) setBound(props.bounds);
  }, [map, props.bounds, setBound]);

  useEffect(() => {
    if (map && props.center && prevCenter.current !== props.center) map.panTo(props.center);
    prevCenter.current = props.center;
  }, [map, props.center]);

  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("bounds_changed", () => {
      if (props.boundsIsSet && map.getZoom() > 17) {
        props.mapZoomChanged(map.getZoom());
        props.mapZoomChanged(18);
      }
    });
    return () => listener.remove();
  }, [map, props.boundsIsSet]); // eslint-disable-line react-hooks/exhaustive-deps

  // While a group is spiderfied, re-render on zoom-end so the fan re-spaces at the new zoom.
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("idle", () => {
      if (props.spiderfyKey != null) setFanZoom(map.getZoom());
    });
    return () => listener.remove();
  }, [map, props.spiderfyKey]);

  return (
    <React.Fragment>
      {href().search("/distance_matrix_master") > 0 && <DistanceMatrixMap setBound={setBound} key={uuidv4()} />}
      {href().search("/location_master") > 0 && <LocationMaster key={uuidv4()} />}
      {href().search("/hazard_point") > 0 && <HazardPoints key={uuidv4()} />}
      {href().search("/route_master") > 0 && <RouteMaster key={uuidv4()} />}
      {(href().search("/route_suggestion") > 0 || href().search("/tracking") > 0) && <RouteSuggestMap map={map} key={uuidv4()} />}
      {href().search("/impact_hazard") > 0 && <HistoryPoint key={uuidv4()} />}
      {(href().search("/location_master") > 0 || href().search("/hazard_point") > 0) && <DrawingMap key="drawingmap" />}
      <SearchPlace key="searchplace" />
      {props.map_loading && <MapLoadingBox />}
    </React.Fragment>
  );
}

function BaseMap(props) {
  return (
    <APIProvider apiKey={Configs[env].MAP_KEY} libraries={["geometry", "places"]}>
      <div style={{ width: "100%", height: "100%" }}>
        <Map
          defaultZoom={props.zoom ? props.zoom : 11}
          defaultCenter={props.center}
          gestureHandling="greedy"
          disableDefaultUI={false}
          onClick={() => props.CLEAR_SELECTING()}
          style={{ width: "100%", height: "100%" }}
        >
          <MapContents {...props} />
        </Map>
      </div>
    </APIProvider>
  );
}

const mapStateToProps = (state) => ({
  drawingMode: state.map.drawingMode,
  center: state.map.center,
  bounds: state.map.bounds,
  boundsIsSet: state.map.boundsIsSet,
  zoom: state.map.zoom,
  link_path: state.ui.activeMenuListItem.subLink,
  map_loading: state.map.map_loading,
  spiderfyKey: state.map.spiderfyKey,
});

const mapActionsToProps = { CLEAR_SELECTING, mapZoomChanged, mapCenterChanged };

export default connect(mapStateToProps, mapActionsToProps)(BaseMap);
